<template>
    <aside class="bloc-acciones">
        <!-- Índice (estilo árbol) -->
        <div class="seccion-indice">
            <h4 class="titulo-seccion">
                <i class="fas fa-sitemap"></i>
                Expediente Médico Electrónico
            </h4>
            
            <nav class="tree-nav">
                <div
v-for="(seccion, index) in indice" :key="seccion.id" class="tree-node" :class="{
                    'is-last': index === indice.length - 1,
                    'seccion-activa': esSeccionActiva(seccion)
                }">
                    <div class="node-content" :class="{ 'activo': esSeccionActiva(seccion) }">
                        <div class="node-info">
                            <span class="node-title">{{ seccion.titulo }}</span>
                            <span class="node-desc">{{ seccion.descripcion }}</span>
                        </div>
                        <i v-if="esSeccionActiva(seccion)" class="fas fa-check-circle activo-seccion-icon"></i>
                    </div>

                    <div class="node-children">
                        <div
v-for="(item, itemIndex) in seccion.items" :key="item.id" class="child-node" :class="{
                            'is-last-child': itemIndex === seccion.items.length - 1,
                            'item-activo': esItemActivo(item)
                        }" @click="ejecutarItem(item)">
                            <div class="child-content" :class="{ 'activo': esItemActivo(item) }">
                                <div class="child-dot"></div>
                                <span class="child-label">{{ item.label }}</span>
                                <i v-if="esItemActivo(item)" class="fas fa-circle-check activo-item-icon"></i>
                            </div>
                        </div>
                    </div>
                </div>
            </nav>
        </div>

        <!-- Historial de navegación (resumen) - Items Modernos -->
        <div v-if="historialNavegacion.length > 0" class="seccion-historial-moderno">
            <div class="historial-header-moderno">
                <span>Historial de navegación</span>
                <span class="contador-historial">{{ historialReciente.length }} de {{ historialNavegacion.length
                }}</span>
            </div>
            <div class="historial-lista-moderno">
                <HistorialItems
v-for="(item, index) in historialReciente" :key="`historial-${index}-${item.timestamp}`"
                    :item="item" @seleccionar="navegarAHistorialModerno" />
            </div>
        </div>

        <!-- Sección Filtros Avanzados -->
        <div class="seccion-filtros">
            <h4 class="titulo-seccion">
                <i class="fas fa-filter-circle-xmark"></i>
                Filtros Avanzados
            </h4>
            <div class="filtros-descriptivos">
                <div class="filtro-item" :class="{ activo: filtroActivo === 'todo' }" @click="emitirFiltro('todo')">
                    <div class="filtro-icon">
                        <i class="fas fa-th-large"></i>
                    </div>
                    <div class="filtro-content">
                        <span class="filtro-titulo">Todos los registros</span>
                        <span class="filtro-descripcion">Vista completa del historial médico</span>
                    </div>
                </div>

                <div
class="filtro-item" :class="{ activo: filtroActivo === 'nota-medica' }"
                    @click="emitirFiltro('nota-medica')">
                    <div class="filtro-icon">
                        <i class="fas fa-file-medical"></i>
                    </div>
                    <div class="filtro-content">
                        <span class="filtro-titulo">Notas Médicas</span>
                        <span class="filtro-descripcion">Evolución clínica y diagnósticos</span>
                    </div>
                </div>

                <div class="filtro-item" :class="{ activo: filtroActivo === 'receta' }" @click="emitirFiltro('receta')">
                    <div class="filtro-icon">
                        <i class="fas fa-prescription-bottle-medical"></i>
                    </div>
                    <div class="filtro-content">
                        <span class="filtro-titulo">Recetas Médicas</span>
                        <span class="filtro-descripcion">Medicamentos y tratamientos</span>
                    </div>
                </div>

                <div
class="filtro-item" :class="{ activo: filtroActivo === 'reporte' }"
                    @click="emitirFiltro('reporte')">
                    <div class="filtro-icon">
                        <i class="fas fa-chart-line"></i>
                    </div>
                    <div class="filtro-content">
                        <span class="filtro-titulo">Reportes</span>
                        <span class="filtro-descripcion">Laboratorios e imágenes médicas</span>
                    </div>
                </div>
            </div>
        </div>
    </aside>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
// ✅ Import absoluto corregido
import { useMedicalWorkspace } from '@/composables/useMedicalWorkspace';
import HistorialItems from './HistorialItems.vue';

const emit = defineEmits(['accion']);

const {
    activarModoAccion,
    historialNavegacion,
    indiceNavegacionActual,
    navegarAtras,
    navegarAdelante,
    modoAccion,
    detalleSeleccionado,
    filtroActual,
    aplicarFiltro
} = useMedicalWorkspace();

const filtroActivo = ref('todo');

// Sincronizar con el estado global
watch(filtroActual, (newVal) => {
    filtroActivo.value = newVal || 'todo';
});

// Índice (estilo libro): secciones y subtemas
const indice = ref([
    {
        id: 'ficha',
        icon: 'fas fa-id-card',
        titulo: 'Ficha',
        descripcion: 'Información personal y de contacto',
        items: [
            { id: 'ver-ficha', label: 'Datos del Paciente', mode: 'perfil-paciente', icon: 'fas fa-user' },
        ],
    },
    {
        id: 'historia',
        icon: 'fas fa-notes-medical',
        titulo: 'Historia clínica',
        descripcion: 'Antecedentes y evolución',
        items: [
            { id: 'ver-historia', label: 'Ver historia clínica', mode: 'historia-clinica', icon: 'fas fa-file-medical' },
        ],
    },
    {
        id: 'notas',
        icon: 'fas fa-file-medical',
        titulo: 'Notas médicas',
        descripcion: 'Registros y evolución',
        items: [
            { id: 'lista-notas', label: 'Historial de notas', mode: 'ver-historial', icon: 'fas fa-list' },
            { id: 'crear-nota', label: 'Crear nota médica', mode: 'nueva-nota', icon: 'fas fa-plus' },
        ],
    },
    {
        id: 'recetas',
        icon: 'fas fa-prescription',
        titulo: 'Recetas',
        descripcion: 'Indicaciones y tratamientos',
        items: [
            { id: 'crear-receta', label: 'Crear receta', mode: 'nueva-receta', icon: 'fas fa-plus' },
        ],
    },
    {
        id: 'archivos',
        icon: 'fas fa-folder-open',
        titulo: 'Archivos',
        descripcion: 'Documentos y reportes',
        items: [
            { id: 'ver-archivos', label: 'Explorar archivos', mode: 'documentos', icon: 'fas fa-folder-open' },
            { id: 'crear-reporte', label: 'Crear reporte', mode: 'nuevo-reporte', icon: 'fas fa-file-image' },
        ],
    },
    {
        id: 'alertas',
        icon: 'fas fa-bell',
        titulo: 'Alertas',
        descripcion: 'Alergias y advertencias',
        items: [
            { id: 'ver-alertas', label: 'Ver alertas', mode: 'alertas-paciente', icon: 'fas fa-bell' },
            { id: 'alergias', label: 'Alergias', mode: 'alergias', icon: 'fas fa-triangle-exclamation' },
        ],
    },
    {
        id: 'graficas',
        icon: 'fas fa-chart-line',
        titulo: 'Gráficas',
        descripcion: 'Signos vitales y tendencias',
        items: [
            { id: 'ver-graficas', label: 'Ver/Registrar signos vitales', mode: 'signos-vitales', icon: 'fas fa-heart-pulse' },
        ],
    },
    {
        id: 'datos',
        icon: 'fas fa-table',
        titulo: 'Datos',
        descripcion: 'Datos tabulares del paciente',
        items: [
            { id: 'ver-datos', label: 'Ver datos del paciente', mode: 'datos-paciente', icon: 'fas fa-database' },
        ],
    },
]);

// 🎯 FUNCIONES para identificar la página/sección actual
const esItemActivo = (item) => {
    // Si hay un modo de acción activo, comparar con el modo del item
    if (modoAccion.value) {
        return item.mode === modoAccion.value;
    }
    // Si hay un detalle seleccionado y el item es de tipo nota médica
    if (detalleSeleccionado.value && detalleSeleccionado.value.tipo === 'nota-medica') {
        return item.mode === 'ver-historial' || item.id === 'lista-notas';
    }
    return false;
};

const esSeccionActiva = (seccion) => {
    // Si hay un modo de acción activo
    if (modoAccion.value) {
        return seccion.items.some(item => item.mode === modoAccion.value);
    }
    // Si hay un detalle seleccionado, verificar si pertenece a alguna sección
    if (detalleSeleccionado.value) {
        const tipo = detalleSeleccionado.value.tipo;
        if (tipo === 'nota-medica') {
            return seccion.id === 'notas';
        } else if (tipo === 'receta') {
            return seccion.id === 'recetas';
        } else if (tipo === 'documento' || tipo === 'reporte') {
            return seccion.id === 'archivos';
        }
    }
    return false;
};

const ejecutarItem = (item) => {
    if (item.mode) activarModo(item.mode);
    if (item.emit) emit('accion', item.emit);
};

const activarModo = (modo) => {
    activarModoAccion(modo);
    emit('accion', { tipo: 'modo-activado', payload: modo });
};

const emitirFiltro = (tipoFiltro) => {
    filtroActivo.value = tipoFiltro;
    
    // 1. Aplicar el filtro globalmente
    aplicarFiltro(tipoFiltro);
    
    // 2. Asegurar que estamos en la vista de historial para ver los resultados
    if (modoAccion.value !== 'ver-historial') {
        activarModoAccion('ver-historial');
    }
    
    // 3. Emitir evento informativo (opcional, ya que manejamos la lógica aquí)
    emit('accion', { tipo: 'filtro-aplicado', payload: tipoFiltro });
};

// Navegar a un punto específico del historial
// eslint-disable-next-line no-unused-vars
const navegarAHistorial = (indice) => {
    const diferencia = indice - indiceNavegacionActual.value;
    if (diferencia > 0) {
        for (let i = 0; i < diferencia; i++) {
            navegarAdelante();
        }
    } else if (diferencia < 0) {
        for (let i = 0; i < Math.abs(diferencia); i++) {
            navegarAtras();
        }
    }
};

// Formatear tiempo relativo
// eslint-disable-next-line no-unused-vars
const formatearTiempo = (timestamp) => {
    const ahora = Date.now();
    const diferencia = ahora - timestamp;
    const segundos = Math.floor(diferencia / 1000);
    const minutos = Math.floor(segundos / 60);
    const horas = Math.floor(minutos / 60);

    if (segundos < 60) return 'Ahora';
    if (minutos < 60) return `${minutos}m`;
    if (horas < 24) return `${horas}h`;
    const dias = Math.floor(horas / 24);
    return `${dias}d`;
};

// Computed para obtener TODO el historial (más recientes primero) - SIN LÍMITE
const historialReciente = computed(() => {
    if (!Array.isArray(historialNavegacion.value)) {
        console.error('❌ BlocAcciones - historialNavegacion NO es un array!');
        return [];
    }

    // Invertir para mostrar los más recientes primero
    const invertido = [...historialNavegacion.value].reverse();
    return invertido;
});

// Función para navegar desde el componente moderno
const navegarAHistorialModerno = (item) => {
    activarModoAccion(item.modo, item.titulo);
};
</script>

<style scoped>
.bloc-acciones {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    background: #ffffff;
    padding: 1.5rem 1rem;
    border-radius: 0;
    border-right: 1px solid #f1f5f9;
    overflow-y: auto;
    overflow-x: hidden;
    height: 100%;
    min-height: 400px;
    max-height: 100%;
    scroll-behavior: smooth;
}

/* Scrollbar personalizado */
.bloc-acciones::-webkit-scrollbar {
    width: 6px;
}

.bloc-acciones::-webkit-scrollbar-track {
    background: transparent;
}

.bloc-acciones::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 10px;
}

.bloc-acciones::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
}

/* Títulos de sección */
.titulo-seccion {
    margin: 0 0 1rem 0;
    color: #64748b;
    font-weight: 700;
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding-bottom: 0.5rem;
    border-bottom: 1px solid #f1f5f9;
}

.titulo-seccion i {
    color: #94a3b8;
    font-size: 0.9rem;
}

/* Buscador de Historia Clínica Fijo */
.buscador-historia-fijo {
    margin-bottom: 1rem;
    padding: 0.5rem;
    background: #f8fafc;
    border-radius: 10px;
    border: 1px solid #f1f5f9;
    box-shadow: none;
}

.buscador-historia-fijo :deep(.enhanced-search-container) {
    margin: 0;
}

.buscador-historia-fijo :deep(.search-input-wrapper) {
    min-height: 44px;
}

.buscador-historia-fijo :deep(.search-filter-input) {
    height: 44px;
    border-radius: 10px;
    border: 1.5px solid #e2e8f0;
    background: white;
    font-size: 0.95rem;
    font-weight: 500;
}

.buscador-historia-fijo :deep(.search-filter-input:focus) {
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

/* Sección Navegación */
.seccion-navegacion {
    background: transparent;
    padding: 0;
    box-shadow: none;
}

/* Navegación en árbol minimalista */
.seccion-indice {
    background: transparent;
    padding: 0;
    box-shadow: none;
}

.tree-nav {
    position: relative;
    padding-left: 1rem;
}

/* Línea vertical principal del árbol - Ajustada sin punto de categoría principal */
.tree-nav::before {
    content: '';
    position: absolute;
    left: 8px;
    top: 0;
    bottom: 2rem;
    width: 1px;
    background: #e2e8f0;
}

.tree-node {
    position: relative;
    margin-bottom: 1rem;
}

.tree-node:last-child {
    margin-bottom: 0;
}

.node-content {
    position: relative;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.5rem 0;
    margin-bottom: 0.5rem;
    transition: all 0.2s ease;
}

/* Punto del nodo principal - ELIMINADO: solo se muestra en subcategorías */

.node-info {
    display: flex;
    flex-direction: column;
    gap: 0.1rem;
}

.node-title {
    font-weight: 600;
    color: #475569;
    font-size: 0.95rem;
    line-height: 1.2;
    transition: color 0.2s ease;
}

/* Estilos para sección activa */
.node-content.activo .node-title {
    color: #0f172a;
    font-weight: 700;
}

.node-content.activo .node-desc {
    color: #64748b;
}

.node-content.activo {
    background: transparent;
    border-radius: 0;
    padding: 0.5rem 0;
    margin: 0;
    border-left: none;
}

/* Animación de pulso solo para child-dot (subcategorías) */
@keyframes pulse-dot {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.7; }
}

.activo-seccion-icon {
    color: #0f172a;
    font-size: 0.9rem;
    margin-left: auto;
}

@keyframes fadeInScale {
    from { opacity: 0; transform: scale(0.9); }
    to { opacity: 1; transform: scale(1); }
}

.node-desc {
    font-size: 0.75rem;
    color: #94a3b8;
    font-weight: 400;
    line-height: 1.3;
}

/* Contenedor de hijos */
.node-children {
    position: relative;
    margin-left: 1rem;
}

/* Línea vertical para conectar los hijos */
.node-children::before {
    content: '';
    position: absolute;
    left: -12px;
    top: -0.5rem;
    height: calc(100% - 0.5rem);
    width: 1px;
    background: #e2e8f0;
}

.child-node {
    position: relative;
    margin-bottom: 0.25rem;
}

.child-node:last-child {
    margin-bottom: 0;
}

/* Línea horizontal que conecta cada hijo */
.child-node::before {
    content: '';
    position: absolute;
    left: -12px;
    top: 50%;
    width: 12px;
    height: 1px;
    background: #e2e8f0;
}

.child-content {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    padding: 0.35rem 0.5rem;
    cursor: pointer;
    transition: all 0.2s ease;
    border-radius: 6px;
    width: 100%;
}

.child-content:hover {
    background: #f8fafc;
    transform: none;
}

/* Punto del nodo hijo */
.child-dot {
    width: 5px;
    height: 5px;
    background: #cbd5e1;
    border-radius: 50%;
    flex-shrink: 0;
    transition: all 0.2s ease;
}

.child-content:hover .child-dot {
    background: #94a3b8;
    transform: none;
}

.child-label {
    font-size: 0.85rem;
    font-weight: 500;
    color: #64748b;
    transition: color 0.2s ease;
}

.child-content:hover .child-label {
    color: #334155;
    font-weight: 500;
}

/* Estilos para item activo */
.child-node.item-activo .child-content {
    background: #f1f5f9;
    border-left: none;
    padding-left: 0.5rem;
    transform: none;
    box-shadow: none;
}

.child-node.item-activo .child-content.activo .child-label {
    color: #0f172a;
    font-weight: 600;
    font-size: 0.85rem;
}

.child-node.item-activo .child-content.activo .child-dot {
    background: #0f172a;
    transform: none;
    box-shadow: none;
    animation: none;
}

.activo-item-icon {
    color: #0f172a;
    font-size: 0.8rem;
    margin-left: auto;
    animation: fadeInScale 0.2s ease;
}

/* Línea de conexión más visible para item activo */
.child-node.item-activo::before {
    background: #cbd5e1;
    width: 12px;
    height: 1px;
    box-shadow: none;
}

.botones-navegacion {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
}

.btn-nav {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 1rem 1.25rem;
    border: 2px solid #e4e7ed;
    background: white;
    border-radius: 12px;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
    overflow: hidden;
}

.btn-nav.destacado {
    background: linear-gradient(135deg, rgba(102, 126, 234, 0.08) 0%, rgba(118, 75, 162, 0.08) 100%);
    border-color: #667eea;
}

.btn-nav::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    opacity: 0;
    transition: opacity 0.3s;
}

.btn-nav:hover {
    border-color: #667eea;
    transform: translateX(6px) translateY(-2px);
    box-shadow: 0 6px 20px rgba(102, 126, 234, 0.25);
}

.btn-nav:hover::before {
    opacity: 0.05;
}

.btn-nav>i {
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    border-radius: 10px;
    font-size: 1.2rem;
    transition: transform 0.3s;
    position: relative;
    z-index: 1;
    flex-shrink: 0;
}

.btn-nav:hover>i {
    transform: scale(1.1) rotate(5deg);
}

.btn-nav-content {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    flex: 1;
}

.btn-nav-titulo {
    font-weight: 700;
    color: #2d3748;
    font-size: 0.95rem;
}

.btn-nav-desc {
    font-size: 0.8rem;
    color: #8492a6;
    font-weight: 500;
}

/* Historial de Navegación */
.historial-navegacion {
    margin-top: 1.25rem;
    padding-top: 1.25rem;
    border-top: 2px solid rgba(102, 126, 234, 0.1);
}

.historial-header {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.75rem;
    color: #4a5568;
    font-size: 0.8rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.historial-header i {
    color: #667eea;
    font-size: 0.9rem;
}

.contador-historial {
    margin-left: auto;
    background: rgba(102, 126, 234, 0.1);
    color: #667eea;
    padding: 2px 8px;
    border-radius: 10px;
    font-size: 0.75rem;
    font-weight: 700;
}

.historial-lista {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

.historial-item {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.65rem 0.85rem;
    border: 2px solid transparent;
    background: rgba(102, 126, 234, 0.03);
    border-radius: 10px;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    text-align: left;
}

.historial-item:hover {
    background: rgba(102, 126, 234, 0.08);
    border-color: rgba(102, 126, 234, 0.2);
    transform: translateX(4px);
}

.historial-item.activo {
    background: linear-gradient(135deg, rgba(102, 126, 234, 0.12) 0%, rgba(118, 75, 162, 0.12) 100%);
    border-color: #667eea;
}

.historial-item>i:first-child {
    color: #8492a6;
    font-size: 0.85rem;
    transition: color 0.3s;
}

.historial-item.activo>i:first-child {
    color: #667eea;
}

.historial-info {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
    min-width: 0;
}

.historial-titulo {
    font-weight: 600;
    color: #2d3748;
    font-size: 0.8rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.historial-tiempo {
    font-size: 0.7rem;
    color: #8492a6;
}

.activo-icon {
    color: #10b981;
    font-size: 0.9rem;
}

/* Sección Acciones Rápidas */
/* Sección historial compacta */
.seccion-historial {
    background: white;
    border-radius: 12px;
    padding: 1.25rem;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
}

/* ========================================
   SECCIÓN HISTORIAL MODERNO
======================================== */
.seccion-historial-moderno {
    background: #f8fafc;
    border-radius: 12px;
    padding: 1rem;
    border: 1px solid #f1f5f9;
    box-shadow: none;
    margin-top: 1rem;
}

.historial-header-moderno {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-bottom: 1rem;
    margin-bottom: 1rem;
    border-bottom: 2px solid rgba(15, 23, 42, 0.06);
}

.historial-header-moderno span:first-child {
    font-size: 0.875rem;
    font-weight: 600;
    color: #1e293b;
    letter-spacing: -0.01em;
}

.historial-header-moderno .contador-historial {
    background: linear-gradient(135deg, #3b82f6 0%, #1e40af 100%);
    color: white;
    padding: 0.25rem 0.625rem;
    border-radius: 6px;
    font-size: 0.75rem;
    font-weight: 600;
    box-shadow: 0 2px 8px rgba(59, 130, 246, 0.2);
}

.historial-lista-moderno {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
}

/* Sección Filtros Avanzados */
.seccion-filtros {
    background: transparent;
    padding: 0;
    box-shadow: none;
}

.filtros-descriptivos {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
}

.filtro-item {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    padding: 0.75rem;
    border: 1px solid transparent;
    background: transparent;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s ease;
    position: relative;
    overflow: hidden;
}

.filtro-item::before {
    content: '';
    position: absolute;
    inset: 0;
    background: #f8fafc;
    opacity: 0;
    transition: opacity 0.2s ease;
}

.filtro-item:hover {
    background: #f8fafc;
    transform: none;
}

.filtro-item:hover::before {
    opacity: 1;
}

.filtro-item.activo {
    background: #f0f9ff;
    border-color: transparent;
    transform: none;
    box-shadow: none;
}

.filtro-item.activo::before {
    opacity: 0;
}

.filtro-icon {
    position: relative;
    z-index: 2;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f1f5f9;
    border-radius: 6px;
    color: #64748b;
    font-size: 0.9rem;
    flex-shrink: 0;
    transition: all 0.2s ease;
}

.filtro-item:hover .filtro-icon {
    background: #e2e8f0;
    color: #475569;
    transform: none;
}

.filtro-item.activo .filtro-icon {
    background: #0ea5e9;
    color: white;
    transform: none;
    box-shadow: none;
}

.filtro-content {
    position: relative;
    z-index: 2;
    display: flex;
    flex-direction: column;
    gap: 0.1rem;
    flex: 1;
    min-width: 0;
}

.filtro-titulo {
    font-weight: 600;
    color: #475569;
    font-size: 0.85rem;
    line-height: 1.3;
}

.filtro-item.activo .filtro-titulo {
    color: #0f172a;
}

.filtro-descripcion {
    font-size: 0.75rem;
    color: #94a3b8;
    font-weight: 400;
    line-height: 1.4;
}

.filtro-item.activo .filtro-descripcion {
    color: #64748b;
    font-weight: 500;
}

/* Efecto de pulso para el filtro activo - ELIMINADO */
.filtro-item.activo .filtro-icon::after {
    display: none;
}

@keyframes pulse-ring {
    0% { transform: scale(1); opacity: 1; }
    100% { transform: scale(1.1); opacity: 0; }
}
</style>
